import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { FiPhone, FiMail } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { Clock, Edit3, ChevronDown, X, Reply } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";
import CrmActivityTimeline from "./CrmActivityTimeline";
import CrmLeadIntelligence from "./CrmLeadIntelligence";
import Customer360Panel from "./Customer360Panel";
import CrmNextBestAction from "./CrmNextBestAction";
import CrmTouchpointBar from "./CrmTouchpointBar";
import CrmCompliancePanel from "./CrmCompliancePanel";
import CrmDocumentTracker from "./CrmDocumentTracker";
import CrmCalendarActions from "./CrmCalendarActions";
import CrmPageShell from "./CrmPageShell";

const CRMMessage = () => {
    const reminderChipClass = (active) =>
        `crm-reminder-sheet__chip ${active ? "crm-reminder-sheet__chip--active" : ""}`;

    const [activeTab, setActiveTab] = useState("Messages");
    const [note, setNote] = useState("");
    const [notesList, setNotesList] = useState([]);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [showNoteBox, setShowNoteBox] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [dateOption, setDateOption] = useState("today"); // 'today'|'tomorrow'|'pick'
    const [timeChoice, setTimeChoice] = useState(null); // 'in30'|'in2h'|'morning'|'evening'|'pick'|null
    const [pickedDate, setPickedDate] = useState(""); // yyyy-mm-dd
    const [pickedTime, setPickedTime] = useState(""); // HH:MM (24h)
    const [saving, setSaving] = useState(false);
    const [isEditingReminder, setIsEditingReminder] = useState(false);
    const [reminderData, setReminderData] = useState(null);
    const [reminderDescription, setReminderDescription] = useState("");
    const [data, setData] = useState([]);
    const [leadContext, setLeadContext] = useState(null);
    const [loadingLead, setLoadingLead] = useState(false);
    const { state } = useLocation();
    const [searchParams] = useSearchParams();
    const queryLeadId = searchParams.get("id");

    const lead = leadContext;
    const leadId = lead?.id ?? state?.leadId ?? queryLeadId;
    const mobile = lead?.mobile ?? state?.mobile;
    const email = lead?.email ?? state?.email;
    const name = lead?.name ?? state?.name ?? "";
    const address = lead?.address ?? state?.address ?? "";
    const city = lead?.city ?? state?.city ?? "";
    const pincode = lead?.pincode ?? state?.pincode ?? "";
    const source = lead?.source ?? lead?.enquiry_source ?? state?.source ?? "";
    const extra = lead?.extra ?? state?.extra ?? "";
    const AssignLeads = lead?.AssignLeads ?? state?.AssignLeads ?? [];
    const typeCustomer = lead?.type_customer ?? state?.type_customer ?? "";
    const leadCreatedAt = lead?.createdAt;

    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const url = API_BASE_URL
    const token = decryptToken("accessToken") || ""

    const buildLeadContextFromState = () => {
        if (!state?.lead && !state?.leadId) return null;
        if (state?.lead) return state.lead;
        return {
            id: state.leadId,
            mobile: state.mobile,
            email: state.email,
            name: state.name,
            address: state.address,
            city: state.city,
            pincode: state.pincode,
            source: state.source,
            extra: state.extra,
            type_customer: state.type_customer,
            AssignLeads: state.AssignLeads,
        };
    };

    const resolveLeadContext = async () => {
        const fromState = buildLeadContextFromState();
        if (fromState?.id && (fromState.mobile || fromState.email || fromState.name)) {
            setLeadContext(fromState);
            return fromState;
        }

        const id = fromState?.id ?? queryLeadId;
        if (!id) {
            setLeadContext(null);
            return null;
        }

        setLoadingLead(true);
        try {
            const response = await fetch(`${url}/leads/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                toast.error(err.message || "Lead not found");
                setLeadContext(null);
                return null;
            }
            const fetched = await response.json();
            setLeadContext(fetched);
            return fetched;
        } catch (error) {
            console.error(error);
            toast.error("Failed to load lead");
            setLeadContext(null);
            return null;
        } finally {
            setLoadingLead(false);
        }
    };


    const fetchLeadData = async (contactMobile, contactEmail) => {
        try {
            const params = new URLSearchParams();
            if (contactMobile) params.set("mobile", contactMobile);
            if (contactEmail) params.set("email", contactEmail);

            if (!contactMobile && !contactEmail) {
                setData([]);
                return;
            }

            const response = await fetch(
                `${url}/leads/getallsingle?${params.toString()}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                toast.error(err.message || "Failed to load contact history");
                setData([]);
                return;
            }

            const res = await response.json();
            setData(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load contact history");
        }
    };

    const openReminderModal = (lead) => {
        setIsEditingReminder(false);

        if (lead.remainder_date && lead.remainder_time) {
            setIsEditingReminder(true);
            setReminderData(lead);

            const today = new Date().toISOString().split("T")[0];
            const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];

            // DATE PREFILL
            if (lead.remainder_date === today) {
                setDateOption("today");
            } else if (lead.remainder_date === tomorrow) {
                setDateOption("tomorrow");
            } else {
                setDateOption("pick");
                setPickedDate(lead.remainder_date);
            }

            // TIME PREFILL
            if (lead.remainder_time === "09:00") setTimeChoice("morning");
            else if (lead.remainder_time === "18:00") setTimeChoice("evening");
            else {
                setTimeChoice("pick");
                setPickedTime(lead.remainder_time);
            }

            // ⭐⭐ DESCRIPTION PREFILL
            setReminderDescription(lead.followup_description || "");

        } else {
            // NEW REMINDER
            setIsEditingReminder(false);
            setDateOption("today");
            setTimeChoice(null);
            setPickedDate("");
            setPickedTime("");
            setReminderDescription(""); // RESET
        }

        setModalOpen(true);
    };



    const fetchNoteData = async (id, contactMobile, contactEmail) => {
        if (!id) return;
        try {
            const response = await fetch(
                `${url}/notec/crmnotes/${id}?mobile=${contactMobile || ""}&email=${contactEmail || ""}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const res = await response.json();
            setNotesList(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error(error);
        }
    };



    const handleSendNote = async () => {
        if (!note.trim() || !leadId) return;

        try {
            const response = await fetch(`${url}/notec`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    remark: note,
                    name: name || lead?.name || "",
                    phone: mobile || lead?.mobile || "",
                    email: email || lead?.email || "",
                    property_id: isLoggedIn?.main_id,
                    user_id: isLoggedIn?.main_id,
                    user_name: isLoggedIn?.full_name || "",
                    extra2: String(leadId),
                }),
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                toast.error(err.message || "Failed to save note");
                return;
            }

            setNote("");
            setEditingNoteId(null);
            await fetchNoteData(leadId, mobile, email);
            toast.success("Note saved");
        } catch (error) {
            console.error(error);
            toast.error("Failed to save note");
        }
    };

    const handleStatusChange = async (newStatus, targetLead) => {
        if (!targetLead?.id) return;
        try {
            const response = await fetch(`${url}/leads/change/${targetLead.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ extra: newStatus }),
            });

            const result = await response.json();

            if (response.ok) {
                if (result.warning) {
                    toast.warning(result.warning);
                } else {
                    toast.success(result.message || "Status updated successfully");
                }

                setLeadContext((prev) => (prev ? { ...prev, extra: newStatus } : prev));
                setData((prev) =>
                    prev.map((item) =>
                        item.id === targetLead.id ? { ...item, extra: newStatus } : item
                    )
                );
            } else {
                toast.error(result.message || "Failed to update status");
            }
        } catch (err) {
            console.error("Error updating status:", err);
            toast.error("An error occurred while updating status");
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const resolved = await resolveLeadContext();
            if (cancelled || !resolved) return;
            await fetchLeadData(resolved.mobile, resolved.email);
            await fetchNoteData(resolved.id, resolved.mobile, resolved.email);
        })();
        return () => { cancelled = true; };
    }, [queryLeadId, state?.leadId]);

    // Prevent background scroll when modal open
    useEffect(() => {
        if (modalOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [modalOpen]);

    // Reset time choice when date option changes
    useEffect(() => {
        setTimeChoice(null);
        setPickedTime("");
        if (dateOption !== "pick") {
            setPickedDate("");
        }
    }, [dateOption]);

    // Format readable date
    const formatReadable = (dateObj) => {
        if (!dateObj) return "";
        return dateObj.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };


    // Compute final selected Date object
    const computeSelectedDateTime = () => {
        const now = new Date();

        if (dateOption === "today") {
            if (timeChoice === "in30") {
                return new Date(now.getTime() + 30 * 60 * 1000);
            }
            if (timeChoice === "in2h") {
                return new Date(now.getTime() + 2 * 60 * 60 * 1000);
            }
            if (timeChoice === "pick" && pickedTime) {
                const [hh, mm] = pickedTime.split(":").map(Number);
                const d = new Date();
                d.setHours(hh, mm, 0, 0);
                return d;
            }
        } else if (dateOption === "tomorrow") {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (timeChoice === "morning") {
                tomorrow.setHours(9, 0, 0, 0);
                return tomorrow;
            }
            if (timeChoice === "evening") {
                tomorrow.setHours(18, 0, 0, 0);
                return tomorrow;
            }
            if (timeChoice === "pick" && pickedTime) {
                const [hh, mm] = pickedTime.split(":").map(Number);
                tomorrow.setHours(hh, mm, 0, 0);
                return tomorrow;
            }
        } else if (dateOption === "pick" && pickedDate) {
            const [y, m, d] = pickedDate.split("-").map(Number);
            const base = new Date(y, m - 1, d);

            if (timeChoice === "morning") {
                base.setHours(9, 0, 0, 0);
                return base;
            }
            if (timeChoice === "evening") {
                base.setHours(18, 0, 0, 0);
                return base;
            }
            if (timeChoice === "pick" && pickedTime) {
                const [hh, mm] = pickedTime.split(":").map(Number);
                base.setHours(hh, mm, 0, 0);
                return base;
            }
            // Default: midnight
            return base;
        }

        return null;
    };

    const selectedDateTime = computeSelectedDateTime();

    // Updated canSave logic
    const canSave =
        (dateOption === "today" && (
            timeChoice === "in30" ||
            timeChoice === "in2h" ||
            (timeChoice === "pick" && pickedTime)
        )) ||
        (dateOption === "tomorrow" && (
            timeChoice === "morning" ||
            timeChoice === "evening" ||
            (timeChoice === "pick" && pickedTime)
        )) ||
        (dateOption === "pick" && pickedDate && (
            timeChoice === "morning" ||
            timeChoice === "evening" ||
            (timeChoice === "pick" && pickedTime)
        ));

    const handleSave = async () => {
        if (!canSave) return;

        setSaving(true);

        const finalISO = selectedDateTime.toISOString();
        const finalDate = finalISO.split("T")[0];
        const finalTime = finalISO.split("T")[1].slice(0, 5);

        const payload = {
            remainder_date: finalDate,
            remainder_time: finalTime,
            followup_description: reminderDescription,  // ⭐ SAVE DESCRIPTION
            followup_status: "Pending"
        };

        try {
            const response = await fetch(`${url}/leads/change/${leadId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                toast.error(err.message || "Failed to save reminder");
                setSaving(false);
                return;
            }

            toast.success(isEditingReminder ? "Reminder updated!" : "Reminder added!");
            setReminderData(payload);
            setLeadContext((prev) => (prev ? { ...prev, ...payload } : prev));

        } catch (err) {
            toast.error("Failed to save reminder");
        }

        setSaving(false);
        setModalOpen(false);
        setIsEditingReminder(false);
    };



    if (loadingLead) {
        return (
            <div className="crm-message-empty p-6 text-center">Loading lead…</div>
        );
    }

    if (!leadId) {
        return (
            <div className="crm-message-empty p-6 text-center">
                No lead selected. Open a lead from the CRM list or use a link with <code>?id=</code>.
            </div>
        );
    }

    return (
        <CrmPageShell variant="detail">
            <div className="crm-message-page">
            <div className="crm-panel crm-message-hero">
                <div className="crm-message-hero__top">
                    <div className="crm-message-hero__info">
                        {leadCreatedAt && (
                            <p className="crm-message-hero__meta">{formatDate(leadCreatedAt)}</p>
                        )}

                        <div className="crm-message-hero__name-row">
                            <h2 className="crm-message-hero__name">{name || "Lead"}</h2>
                            {typeCustomer && (
                                <span className="crm-message-hero__badge">{typeCustomer}</span>
                            )}
                        </div>

                        <div className="crm-message-hero__details">
                            {AssignLeads && AssignLeads.length > 0 ? (
                                <p>
                                    <strong>Assigned to:</strong>{" "}
                                    {AssignLeads.map((assign, index) => (
                                        <span key={assign.id}>
                                            {assign.staff_name.replace(/\(ID:.*?\)/, "").trim()}
                                            {index < AssignLeads.length - 1 ? ", " : ""}
                                        </span>
                                    ))}
                                </p>
                            ) : null}
                            <p>
                                <strong>Source:</strong> {source}
                            </p>
                            <p>
                                <strong>Address:</strong>
                                <br />
                                {address} {city} {pincode}
                            </p>
                        </div>
                    </div>

                    <div className="crm-message-hero__contacts">
                        <button
                            type="button"
                            onClick={() => {
                                if (mobile) window.location.href = `tel:${mobile}`;
                            }}
                            className="crm-message-contact-btn crm-message-contact-btn--phone"
                            aria-label="Call lead"
                        >
                            <FiPhone />
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                if (email) window.location.href = `mailto:${email}`;
                            }}
                            className="crm-message-contact-btn crm-message-contact-btn--email"
                            aria-label="Email lead"
                        >
                            <FiMail />
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                if (mobile) {
                                    const phone = mobile.startsWith("+") ? mobile : `+91${mobile}`;
                                    window.open(`https://wa.me/${phone}`, "_blank");
                                }
                            }}
                            className="crm-message-contact-btn crm-message-contact-btn--whatsapp"
                            aria-label="WhatsApp lead"
                        >
                            <FaWhatsapp />
                        </button>
                    </div>
                </div>

                <div className="crm-message-hero__actions">
                    <button
                        type="button"
                        onClick={() => openReminderModal(lead || { id: leadId, remainder_date: reminderData?.remainder_date, remainder_time: reminderData?.remainder_time, followup_description: reminderData?.followup_description })}
                        className="crm-message-action-btn"
                    >
                        <Clock size={16} />
                        Set Reminder
                    </button>

                    <div className="crm-message-status-select-wrap">
                        <select
                            name="extra"
                            value={extra || ""}
                            onChange={(e) => handleStatusChange(e.target.value, lead || { id: leadId, extra })}
                            className="crm-message-status-select"
                        >
                            <option value="">Select status</option>
                            <option value="Pending">Pending</option>
                            <option value="Converted">Converted</option>
                            <option value="Not Interested">Not Interested</option>
                            <option value="In Progress">In Progress</option>
                        </select>
                        <ChevronDown size={16} />
                    </div>
                </div>
            </div>

            <CrmTouchpointBar lead={lead || { id: leadId, mobile, email }} />
            <CrmCompliancePanel leadId={leadId} lead={lead} />
            <CrmCalendarActions leadId={leadId} leadName={name} />
            <CrmDocumentTracker leadId={leadId} />

            {/* Tabs + content */}
            <CrmLeadIntelligence leadId={leadId} mobile={mobile} email={email} />
            {lead && (
                <div className="px-1 py-2">
                    <CrmNextBestAction lead={lead} />
                </div>
            )}
            <Customer360Panel leadId={leadId} />

            <div className="crm-panel crm-panel__body">
                <div className="crm-message-tabs">
                    {["Messages", "Notes", "Activities"].map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => setActiveTab(tab)}
                            className={`crm-message-tab ${activeTab === tab ? "crm-message-tab--active" : ""}`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <div className="crm-message-tab-content">
                    {activeTab === "Messages" && (
                        <div>
                            {data.length === 0 ? (
                                <p className="crm-message-empty">No messages found.</p>
                            ) : (
                                data.map((msg, index) => (
                                    <div key={index} className="crm-message-item">
                                        <p className="crm-message-item__date">
                                            {formatDate(msg.createdAt)}
                                        </p>
                                        <p className="crm-message-item__text">
                                            {msg.remark || "No remark"}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                    )}


                    {activeTab === "Notes" && (
                        <div>
                            <div className="flex flex-col gap-2 mb-3">
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Write a note…"
                                    rows={2}
                                    className="crm-message-textarea"
                                />
                                <button
                                    type="button"
                                    onClick={handleSendNote}
                                    className="crm-message-primary-btn"
                                >
                                    Save note
                                </button>
                            </div>
                            {notesList.length === 0 ? (
                                <p className="crm-message-empty">No notes found.</p>
                            ) : (
                                notesList.map((msg, index) => (
                                    <div key={msg.id ?? index} className="crm-message-item">
                                        <p className="crm-message-item__date">{formatDate(msg.createdAt)}</p>
                                        <p className="crm-message-item__text">{msg.remark || "No note"}</p>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === "Activities" && (
                        <div className="space-y-3">
                            <CrmActivityTimeline leadId={leadId} />
                            {lead?.remainder_date && (
                                <button
                                    type="button"
                                    className="crm-message-primary-btn"
                                    onClick={() => openReminderModal(lead)}
                                >
                                    Edit reminder
                                </button>
                            )}
                        </div>
                    )}

                </div>
            </div>
            </div>

            {/* Modal (Bottom Sheet) */}
            {modalOpen && (
                <>
                    <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setModalOpen(false)} />
                    <div className="fixed bottom-0 left-0 right-0 z-[70]">
                        <div className="mx-auto max-w-3xl">
                            <div className="crm-reminder-sheet">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <h3 className="crm-reminder-sheet__title">Set Reminder</h3>
                                    </div>
                                    <button type="button" onClick={() => setModalOpen(false)} className="crm-reminder-sheet__close">
                                        <X size={18} />
                                    </button>
                                </div>

                                {/* Date Selection */}
                                <div className="mb-3">
                                    <p className="crm-reminder-sheet__label">Select date</p>
                                    <div className="flex gap-2 flex-wrap">
                                        <button
                                            type="button"
                                            onClick={() => setDateOption("today")}
                                            className={reminderChipClass(dateOption === "today")}
                                        >
                                            Today
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDateOption("tomorrow")}
                                            className={reminderChipClass(dateOption === "tomorrow")}
                                        >
                                            Tomorrow
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDateOption("pick")}
                                            className={reminderChipClass(dateOption === "pick")}
                                        >
                                            Pick a date
                                        </button>
                                    </div>
                                </div>

                                {/* Custom Date Picker */}
                                {dateOption === "pick" && (
                                    <div className="mb-3">
                                        <p className="crm-reminder-sheet__label">Choose a date</p>
                                        <input
                                            type="date"
                                            value={pickedDate}
                                            onChange={(e) => setPickedDate(e.target.value)}
                                            className="crm-reminder-sheet__input"
                                        />
                                        {pickedDate && (
                                            <p className="crm-reminder-sheet__label mt-2">Selected: {new Date(pickedDate).toDateString()}</p>
                                        )}
                                    </div>
                                )}

                                {/* Time Selection */}
                                <div className="mb-3">
                                    <p className="crm-reminder-sheet__label">Select time</p>

                                    {/* Always show quick options based on date, but keep them visible after selection */}
                                    {(dateOption === "today" || dateOption === "tomorrow" || (dateOption === "pick" && pickedDate)) && (
                                        <div className="flex gap-2 flex-wrap mb-3">
                                            {/* Today Quick Options */}
                                            {dateOption === "today" && (
                                                <>
                                                    <button
                                                        onClick={() => setTimeChoice("in30")}
                                                        className={reminderChipClass(timeChoice === "in30")}
                                                    >
                                                        In 30 mins
                                                    </button>
                                                    <button
                                                        onClick={() => setTimeChoice("in2h")}
                                                        className={reminderChipClass(timeChoice === "in2h")}
                                                    >
                                                        In 2 hours
                                                    </button>
                                                </>
                                            )}

                                            {/* Tomorrow or Custom Date Quick Options */}
                                            {(dateOption === "tomorrow" || (dateOption === "pick" && pickedDate)) && (
                                                <>
                                                    <button
                                                        onClick={() => setTimeChoice("morning")}
                                                        className={reminderChipClass(timeChoice === "morning")}
                                                    >
                                                        Morning (09:00)
                                                    </button>
                                                    <button
                                                        onClick={() => setTimeChoice("evening")}
                                                        className={reminderChipClass(timeChoice === "evening")}
                                                    >
                                                        Evening (18:00)
                                                    </button>
                                                </>
                                            )}

                                            {/* Always show "Pick a time" button */}
                                            <button
                                                onClick={() => setTimeChoice("pick")}
                                                className={reminderChipClass(timeChoice === "pick")}
                                            >
                                                Pick a time
                                            </button>
                                        </div>
                                    )}

                                    {/* Custom Time Picker - only show when "Pick a time" is selected */}
                                    {timeChoice === "pick" && (
                                        <div className="mt-2">
                                            <input
                                                type="time"
                                                value={pickedTime}
                                                onChange={(e) => setPickedTime(e.target.value)}
                                                className="crm-reminder-sheet__input"
                                            />
                                            {pickedTime && (
                                                <p className="crm-reminder-sheet__label mt-1">Chosen time: {pickedTime}</p>
                                            )}
                                        </div>
                                    )}

                                    {timeChoice && timeChoice !== "pick" && (
                                        <div className="mt-2 crm-reminder-sheet__label">
                                            Selected:{" "}
                                            <span style={{ color: "var(--text)" }}>
                                                {timeChoice === "in30" && "In 30 minutes"}
                                                {timeChoice === "in2h" && "In 2 hours"}
                                                {timeChoice === "morning" && "Morning (09:00)"}
                                                {timeChoice === "evening" && "Evening (18:00)"}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <p className="crm-reminder-sheet__label">Reminder note (optional)</p>
                                    <textarea
                                        value={reminderDescription}
                                        onChange={(e) => setReminderDescription(e.target.value)}
                                        placeholder="What to follow up on…"
                                        rows={2}
                                        className="crm-reminder-sheet__input"
                                    />
                                </div>

                                <div className="mb-3 pt-3 border-t border-[var(--border)]">
                                    <p className="crm-reminder-sheet__label">Preview</p>
                                    <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
                                        {selectedDateTime ? (
                                            <>
                                                <div style={{ color: "var(--text)", fontWeight: 600 }}>Reminder set for</div>
                                                <div className="crm-reminder-sheet__label">{formatReadable(selectedDateTime)}</div>
                                            </>
                                        ) : (
                                            <div className="crm-reminder-sheet__label">No time selected yet</div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setModalOpen(false)}
                                        className="crm-reminder-sheet__btn crm-reminder-sheet__btn--ghost"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        disabled={!canSave || saving}
                                        className="crm-reminder-sheet__btn crm-reminder-sheet__btn--primary"
                                    >
                                        {saving ? "Saving..." : "Save Reminder"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </CrmPageShell>
    );
};

export default CRMMessage;